import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { BffOe, OeKpis } from "aint-types";
import { CALCULATED_FIELDS, calculateValue } from "aint-utils";

import { OeKpisDataGridRow } from "./oe-kpis-data-grid";
import {
  INDEPENDENT_OE_KPI_FIELDS,
  OE_KPI_FIELDS_WITH_DEPENDNCIES,
  OE_KPIS_DATA_GRID_ROWS,
} from "./oe-kpis-data-grid-constants";

const CELL_WIDTH = 100;

/**
 * Generates the columns (headers) for the datagrid.
 * The first column is the static "KPI" row.
 * Generates quarters for each year from startYear to endYear.
 * For each quarter there is a planned and an actual column.
 */
export function generateColumns({
  startYear,
  endYear,
  editable,
}: {
  startYear: number;
  endYear: number;
  editable: boolean;
}): GridColDef<OeKpisDataGridRow>[] {
  const cols: GridColDef<OeKpisDataGridRow>[] = [
    {
      field: "kpi",
      headerName: "KPI",
      width: 230,
      type: "string",
      renderCell: (params) => renderKPICell(params.value),
      editable: false,
    },
  ];
  let year = startYear;

  while (year <= endYear) {
    if (year === 2023) {
      cols.push({
        field: `2023-Q4-actual`,
        renderHeader: ({ field }) => renderHeader(field),
        renderCell: (params) => renderCell(params),
        type: "number",
        width: CELL_WIDTH,
        editable,
      });

      year++;
      continue;
    }

    if (year === 2024) {
      cols.push({
        field: `2024-Q4-plan`,
        renderHeader: ({ field }) => renderHeader(field),
        renderCell: (params) => renderCell(params),
        type: "number",
        width: CELL_WIDTH,
        editable,
      });
      cols.push({
        field: `2024-Q4-actual`,
        renderHeader: ({ field }) => renderHeader(field),
        renderCell: (params) => renderCell(params),
        type: "number",
        width: CELL_WIDTH,
        editable,
      });
      year++;
      continue;
    }

    ["Q1", "Q2", "Q3", "Q4"].forEach((quarter) => {
      cols.push({
        field: `${year}-${quarter}-plan`,
        renderHeader: ({ field }) => renderHeader(field),
        renderCell: (params) => renderCell(params),
        type: "number",
        width: CELL_WIDTH,
        editable,
      });
      cols.push({
        field: `${year}-${quarter}-actual`,
        type: "number",
        width: CELL_WIDTH,
        editable,
        renderHeader: ({ field }) => renderHeader(field),
        renderCell: (params) => renderCell(params),
      });
    });

    year++;
  }

  return cols;
}

/**
 * Generates the data rows for the grid by going through the INDEPENDENT_OE_KPI_FIELDS and OE_KPI_FIELDS_WITH_DEPENDNCIES constants.
 * These contain a field name for each of the KPIs in the OE. The order the rows are generated in is important to ensure
 * fields with a dependency on other fields have their dependency calculated first.
 * Then we iterate over the KPI data of the oe to populate the row for the datagrid.
 *
 * We return the generated rows but also a modified copy of the OE that contains all the calculated values.
 */
export function generateRows({
  startYear,
  endYear,
  operatingEntity,
  editable,
}: {
  startYear: number;
  endYear: number;
  operatingEntity?: BffOe;
  editable: boolean;
}): { updatedOe?: BffOe; rows: OeKpisDataGridRow[] } {
  if (!operatingEntity) {
    return { rows: [], updatedOe: operatingEntity };
  }

  const rows: OeKpisDataGridRow[] = [];

  // Calculate the independent fields first,
  // so the fields with dependencies have their dependencies calculated when its their turn
  [...INDEPENDENT_OE_KPI_FIELDS, ...OE_KPI_FIELDS_WITH_DEPENDNCIES].forEach(
    (field) => {
      const row = OE_KPIS_DATA_GRID_ROWS.find((row) => row.field === field);

      if (!row) {
        return;
      }

      const { id, kpi } = row;

      // If the kpi doesnt have a value, we add an empty row.
      if (!operatingEntity[field]) {
        rows.push({
          id,
          kpi,
          field,
        });
        return;
      }

      const fields: Record<string, number | undefined> = {};
      let year = startYear;

      while (year <= endYear) {
        for (const quarter of [1, 2, 3, 4]) {
          // Get the value for the KPI. This might be just the value out of the OE, or a calculated value depending on other KPIs.
          const { planValue, actualValue } = calculateValue({
            field,
            year,
            quarter,
            operatingEntity,
          });

          // Set the value in the fields array of the row.
          fields[`${year}-Q${quarter}-plan`] = planValue;
          fields[`${year}-Q${quarter}-actual`] = actualValue;

          // Now set the value into the operating entity, to be used further down the line to calculate dependent kpis.
          const index = operatingEntity[field].findIndex(
            (kpiObj) => +kpiObj.year === year && kpiObj.quarter === quarter
          );

          if (index !== -1) {
            operatingEntity[field][index] = {
              year: year.toString(),
              quarter,
              planValue,
              actualValue,
            };
          } else {
            operatingEntity[field].push({
              year: year.toString(),
              quarter,
              planValue,
              actualValue,
            });
          }
        }
        year++;
      }

      rows.push({
        id,
        // If we are in edit mode, fields with id 1, 7, 8 should have an "*" appended to the label.
        kpi: editable && [1, 7, 8].includes(id) ? kpi + "*" : kpi,
        field,
        ...fields,
      });
    }
  );

  return {
    rows: rows.sort(({ id: ida }, { id: idb }) => ida - idb),
    updatedOe: operatingEntity,
  };
}

/**
 * Converts the DataGrids rows back to the OeKpis Datatype to be sent to the Backend.
 */
export function extractKpisFromRows(
  rows: OeKpisDataGridRow[],
  includeCalculatedValues: boolean
): OeKpis {
  const kpis: Partial<OeKpis> = {};

  for (const row of rows) {
    const field = row.field;

    if (!includeCalculatedValues && CALCULATED_FIELDS[field] === true) {
      continue;
    }

    for (const [key, value] of Object.entries(row)) {
      if (["id", "kpi", "field"].includes(key) || !value) {
        continue;
      }

      const [year, quarterString, actualOrPlanned] = key.split("-");
      const quarter = +quarterString.charAt(1);

      if (
        !includeCalculatedValues &&
        Array.isArray(CALCULATED_FIELDS[field]) &&
        CALCULATED_FIELDS[field]?.includes(
          `${quarterString}-${actualOrPlanned}`
        )
      ) {
        continue;
      }

      if (!kpis[field]) {
        kpis[field] = [];
      }

      const index = kpis[field].findIndex(
        (kpi) => kpi.year === year && kpi.quarter === quarter
      );

      if (kpis[field][index]) {
        kpis[field][index] = {
          ...kpis[field][index],
          ...(actualOrPlanned === "plan"
            ? { planValue: +value }
            : { actualValue: +value }),
        };
      } else {
        kpis[field].push({
          year,
          quarter,
          ...(actualOrPlanned === "plan"
            ? { planValue: +value }
            : { actualValue: +value }),
        });
      }
    }
  }

  return kpis as OeKpis;
}

/**
 * Determines is a field is to be editable based on the rules set in the CALCULATED_FIELDS constant.
 */
export function isCellEditable({
  field,
  quarter,
  planOrActual,
}: {
  field: string;
  quarter: number;
  planOrActual: string;
}) {
  if (CALCULATED_FIELDS[field] === true) {
    return false;
  }

  if (CALCULATED_FIELDS[field]?.includes(`Q${quarter}-${planOrActual}`)) {
    return false;
  }
  return true;
}

/**
 * Renders the header of the data grid
 */
function renderHeader(fieldName: string) {
  const [year, quarter, planOrActual] = fieldName.split("-");

  return (
    <div style={{ fontWeight: 700, textAlign: "center" }}>
      {`${year !== "2023" && year !== "2024" ? "YTD" + quarter : ""} ${year}`}
      <br />
      {planOrActual.toUpperCase()}
    </div>
  );
}

/**
 * Renders a the cell of a datagrid as a simple div with a custom CSS class.
 */
function renderCell(
  params: GridRenderCellParams<
    OeKpisDataGridRow,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    GridTreeNodeWithRender
  >
) {
  const value = params.value;

  return <div className="CustomCell">{value ?? "N.P."}</div>;
}

/**
 * Renders the "static" kpi cell a a simple div
 */
function renderKPICell(value: string) {
  return <div style={{ fontWeight: 700, margin: "12px 0" }}>{value}</div>;
}
